import { useQuery } from "@apollo/client";
import { Icon, SearchIcon, AddIcon } from "@chakra-ui/icons";
import {
  useMediaQuery,
  useDisclosure,
  SimpleGrid,
  Flex,
  InputGroup,
  InputLeftAddon,
  Input,
  Popover,
  PopoverTrigger,
  Button,
  Portal,
  PopoverContent,
  PopoverArrow,
  PopoverHeader,
  PopoverCloseButton,
  PopoverBody,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  ButtonGroup,
  IconButton,
  Stack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Center,
  Spinner,
  Text,
  Box,
} from "@chakra-ui/react";
import { RangeDatepicker } from "chakra-dayzed-datepicker";
import { debounce, get } from "lodash";
import moment from "moment";
import React, { useCallback, useState } from "react";
import { Helmet } from "react-helmet";
import { BsThreeDotsVertical } from "react-icons/bs";
import { FaSync } from "react-icons/fa";
import { MdExposurePlus2, MdFilterList } from "react-icons/md";
import ReactSelect from "react-select";
import { GET_COMMODITIES_PUBLIC } from "../../pages/public/queries";
import { GET_COMMODITIES } from "../../pages/user/queries";
import { PRODUCT_CATEGORIES } from "../../utils/constants";
import Pagination from "../common/Pagination";
import CreateCommodity from "../modals/commodity/create/CreateCommodity";
import CreateMassCommodity from "../modals/commodity/create/CreateMassCommodity";
import CommoditiesTableContainer from "./CommoditiesTableContainer";

type CommoditiesTableProps = {
  isPublic?: boolean;
};

const CommoditiesTable = (props: CommoditiesTableProps) => {
  const { isPublic } = props;
  const [isLargerThan900] = useMediaQuery("(min-width: 900px)");
  const [search, setSearch] = useState("");
  const [categories, setCategories] = useState([]);
  const [status, setStatus] = useState([]);

  const [refetchLoading, setRefetchLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [selectedDates, setSelectedDates] = useState<Date[] | any>([
    new Date(moment(new Date()).subtract(1, "month").format()),
    new Date(moment(new Date()).add(1, "month").format()),
  ]);

  const [range, setRange] = useState<{
    min: number | any;
    max?: number | any;
  }>({
    min: 1,
  });

  const { data, loading, refetch } = useQuery(GET_COMMODITIES, {
    variables: {
      args: {
        ...range,
        categories,
        query: search,
        status,
        startDate: selectedDates[0],
        endDate: selectedDates[1],
        pagination: {
          page,
          size: 20,
        },
      },
    },
    fetchPolicy: "network-only",
  });

  const debounceFn = useCallback(
    debounce((val) => {
      setSearch(val);
      setPage(1);
    }, 500),
    []
  );

  const debounceRange = useCallback(
    debounce((val) => {
      setRange(val);
      setPage(1);
    }, 500),
    []
  );

  const debounceCategories = useCallback(
    debounce((val) => {
      setCategories(val);
      setPage(1);
    }, 500),
    []
  );
  const debounceStatus = useCallback(
    debounce((val) => {
      setStatus(val);
      setPage(1);
    }, 500),
    []
  );

  const onSearch = (val: any) => {
    debounceFn(val);
  };

  const onChangeRange = (val: any) => {
    debounceRange(val);
  };

  const onChangeCategories = (val: any) => {
    debounceCategories(val);
  };

  const onChangeStatus = (val: any) => {
    debounceStatus(val);
  };

  const {
    isOpen: isCreateModalOpen,
    onOpen: onOpenCreateModal,
    onClose: onCloseCreateModal,
  } = useDisclosure();

  const {
    isOpen: isMassCreateModalOpen,
    onOpen: onOpenMassCreateModal,
    onClose: onCloseMassCreateModal,
  } = useDisclosure();

  // const {
  //   isOpen: isFilterOen,
  //   onOpen: openFilterModal,
  //   onClose: closeFilterModal,
  // } = useDisclosure();

  const refetchCommodities = () => {
    setRefetchLoading(true);
    refetch()
      .then(() => setRefetchLoading(false))
      .finally(() => setRefetchLoading(false));
  };

  const commodities = get(data, "commodities.items", []);
  const meta = get(data, "commodities.meta", []);

  return (
    <>
      {isLargerThan900 ? (
        <SimpleGrid columns={[1, 2]}>
          <Pagination
            meta={meta}
            page={page}
            setPage={setPage}
            isDisabled={loading}
          />

          <Flex alignItems="center" justifyContent="space-between">
            <InputGroup mx="2">
              <InputLeftAddon
                rounded="md"
                children={<Icon size="2" as={SearchIcon} />}
              />
              <Input
                rounded="md"
                focusBorderColor="green.500"
                onChange={(e) => {
                  onSearch(e.target.value);
                }}
              />
            </InputGroup>
            <Flex>
              <Popover>
                <PopoverTrigger>
                  <Button rightIcon={<MdFilterList />}>Filters</Button>
                </PopoverTrigger>
                <Portal>
                  <PopoverContent>
                    <PopoverArrow />
                    <PopoverHeader>Filters</PopoverHeader>
                    <PopoverCloseButton />
                    <PopoverBody>
                      <Flex p="2" flexDirection="column">
                        <Flex>
                          <Flex flexDirection="column">
                            <Text ml="2">Min</Text>
                            <NumberInput
                              size="md"
                              min={1}
                              defaultValue={range.min}
                              onChange={(price: any) => {
                                onChangeRange({
                                  ...range,
                                  min: Number(price),
                                });
                              }}
                              mx="2"
                            >
                              <NumberInputField />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          </Flex>
                          <Flex flexDirection="column">
                            <Text ml="2">Max</Text>
                            <NumberInput
                              size="md"
                              min={1}
                              defaultValue={range?.max || null}
                              onChange={(price: any) => {
                                onChangeRange({
                                  ...range,
                                  max: Number(price),
                                });
                              }}
                              mx="2"
                            >
                              <NumberInputField />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          </Flex>
                        </Flex>
                      </Flex>

                      <Flex p="4" flexDirection="column">
                        <Text>Date Range</Text>

                        <RangeDatepicker
                          selectedDates={selectedDates}
                          onDateChange={setSelectedDates}
                          propsConfigs={{
                            dayOfMonthBtnProps: {
                              defaultBtnProps: {
                                _hover: {
                                  background: "primary",
                                  color: "white",
                                },
                              },
                              selectedBtnProps: {
                                background: "primary",
                                color: "white",
                              },
                              // todayBtnProps: {
                              //   background: "gray.100",
                              // },
                            },
                            inputProps: {
                              size: "md",
                            },
                          }}
                        />
                      </Flex>

                      <Flex p="4" flexDirection="column">
                        <Text>Categories</Text>
                        <ReactSelect
                          isMulti
                          name="colors"
                          closeMenuOnSelect={false}
                          options={PRODUCT_CATEGORIES}
                          className="basic-multi-select"
                          classNamePrefix="select"
                          onChange={(val: any) => {
                            onChangeCategories(val.map((v: any) => v.value));
                          }}
                        />
                      </Flex>
                      <Flex p="4" flexDirection="column">
                        <Text ml="2">Status</Text>

                        <ReactSelect
                          isMulti
                          closeMenuOnSelect={false}
                          onChange={(val: any) => {
                            onChangeStatus(val.map((v: any) => v.value));
                          }}
                          options={[
                            {
                              label: "Active",
                              value: "active",
                            },
                            {
                              label: "Archived",
                              value: "archived",
                            },
                          ]}
                        />
                      </Flex>
                    </PopoverBody>
                  </PopoverContent>
                </Portal>
              </Popover>

              <ButtonGroup isAttached mx="2">
                {!isPublic && (
                  <>
                    <Button onClick={onOpenCreateModal}>New</Button>
                    <IconButton
                      aria-label="More"
                      bg="gray.200"
                      icon={<AddIcon />}
                      onClick={onOpenMassCreateModal}
                    />
                  </>
                )}
                <ButtonGroup>
                  {refetchLoading ? (
                    <Button ml="2">
                      <Spinner size="sm" />
                    </Button>
                  ) : (
                    <IconButton
                      aria-label="More"
                      bg="gray.200"
                      icon={<FaSync />}
                      isDisabled={loading}
                      onClick={refetchCommodities}
                      ml="2"
                    />
                  )}
                </ButtonGroup>
              </ButtonGroup>
            </Flex>
          </Flex>
        </SimpleGrid>
      ) : (
        <Stack>
          <InputGroup p="3">
            <InputLeftAddon
              rounded="md"
              children={<Icon size="2" as={SearchIcon} />}
            />
            <Input
              rounded="md"
              focusBorderColor="green.500"
              onChange={(e) => {
                onSearch(e.target.value);
              }}
            />
          </InputGroup>

          <Flex alignItems="center" justifyContent="space-between">
            <Pagination
              meta={meta}
              page={page}
              setPage={setPage}
              isDisabled={loading}
              showBottomTotal={true}
              showTotal={false}
              size="md"
            />
            <Box p="3">
              <Popover>
                <PopoverTrigger>
                  <IconButton
                    aria-label="filters_icons"
                    icon={<MdFilterList />}
                    mx="3"
                  />
                </PopoverTrigger>
                <Portal>
                  <PopoverContent>
                    <PopoverArrow />
                    <PopoverHeader>Filters</PopoverHeader>
                    <PopoverCloseButton />
                    <PopoverBody>
                      <Flex p="2" flexDirection="column">
                        <Flex>
                          <Flex flexDirection="column">
                            <Text ml="2">Min</Text>
                            <NumberInput
                              size="md"
                              min={1}
                              defaultValue={range.min}
                              onChange={(price: any) => {
                                onChangeRange({
                                  ...range,
                                  min: Number(price),
                                });
                              }}
                              mx="2"
                            >
                              <NumberInputField />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          </Flex>
                          <Flex flexDirection="column">
                            <Text ml="2">Max</Text>
                            <NumberInput
                              size="md"
                              min={1}
                              defaultValue={range?.max || null}
                              onChange={(price: any) => {
                                onChangeRange({
                                  ...range,
                                  max: Number(price),
                                });
                              }}
                              mx="2"
                            >
                              <NumberInputField />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          </Flex>
                        </Flex>

                        <Flex p="2" flexDirection="column">
                          <Text>Date Range</Text>

                          <RangeDatepicker
                            selectedDates={selectedDates}
                            onDateChange={setSelectedDates}
                            propsConfigs={{
                              dayOfMonthBtnProps: {
                                defaultBtnProps: {
                                  _hover: {
                                    background: "primary",
                                    color: "white",
                                  },
                                },
                                selectedBtnProps: {
                                  background: "primary",
                                  color: "white",
                                },
                              },
                              inputProps: {
                                size: "md",
                              },
                            }}
                          />
                        </Flex>
                        <Flex p="4" flexDirection="column">
                          <Text>Categories</Text>
                          <ReactSelect
                            isMulti
                            name="colors"
                            closeMenuOnSelect={false}
                            options={PRODUCT_CATEGORIES}
                            className="basic-multi-select"
                            classNamePrefix="select"
                            onChange={(val: any) => {
                              onChangeCategories(val.map((v: any) => v.value));
                            }}
                          />
                        </Flex>

                        <Flex p="4" flexDirection="column">
                          <Text ml="2">Status</Text>

                          <ReactSelect
                            isMulti
                            closeMenuOnSelect={false}
                            onChange={(val: any) => {
                              onChangeStatus(val.map((v: any) => v.value));
                            }}
                            options={[
                              {
                                label: "Active",
                                value: "active",
                              },
                              {
                                label: "Archived",
                                value: "archived",
                              },
                            ]}
                          />
                        </Flex>
                      </Flex>
                    </PopoverBody>

                    {/* <PopoverFooter>This is the footer</PopoverFooter> */}
                  </PopoverContent>
                </Portal>
              </Popover>
              <Menu size="md">
                <MenuButton size="md" as={Button}>
                  <BsThreeDotsVertical />
                </MenuButton>
                <MenuList>
                  {!isPublic && (
                    <>
                      <MenuItem icon={<AddIcon />} onClick={onOpenCreateModal}>
                        Create
                      </MenuItem>

                      <MenuItem
                        icon={<MdExposurePlus2 />}
                        onClick={onOpenMassCreateModal}
                      >
                        Mass Create
                      </MenuItem>
                    </>
                  )}

                  <MenuItem onClick={refetchCommodities} icon={<FaSync />}>
                    Refresh
                  </MenuItem>
                </MenuList>
              </Menu>
            </Box>
          </Flex>
        </Stack>
      )}

      <Box mt="6">
        {loading ? (
          <Center py="5">
            <Spinner />
          </Center>
        ) : isLargerThan900 ? (
          <Box p="5" height="70vh" overflow="auto-scroll">
            {refetchLoading && (
              <Center py="5">
                <Spinner />
              </Center>
            )}
            <CommoditiesTableContainer
              commodities={commodities}
              isPublic={isPublic}
            />
          </Box>
        ) : (
          <>
            {refetchLoading && (
              <Center py="5">
                <Spinner />
              </Center>
            )}
            <CommoditiesTableContainer
              commodities={commodities}
              isPublic={isPublic}
            />
          </>
        )}
      </Box>

      <CreateCommodity
        isOpen={isCreateModalOpen}
        onOpen={onOpenCreateModal}
        onClose={onCloseCreateModal}
        refetch={refetchCommodities}
      />
      <CreateMassCommodity
        isOpen={isMassCreateModalOpen}
        onOpen={onOpenMassCreateModal}
        onClose={onCloseMassCreateModal}
        refetch={refetchCommodities}
      />
      {/* <FiltersDrawer
        isOpen={isFilterOen}
        onOpen={openFilterModal}
        onClose={closeFilterModal}
        onChangeRange={onChangeRange}
        onChangeCategories={onChangeCategories}
        onChangeStatus={onChangeStatus}
        selectedDates={selectedDates}
        setSelectedDates={setSelectedDates}
        range={range}
      /> */}
    </>
  );
};

export default CommoditiesTable;
